import React, { useEffect, useState, Suspense, lazy } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MapPin, Building2, Star, MessageSquarePlus, RefreshCw, BadgeCheck, Loader2 } from "lucide-react";
import { api, formatApiError, prefixUrl } from "@/lib/api";
import PropertyCard from "@/components/PropertyCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";

// Map bundle loads only when the profile renders (leaflet stays out of the shell).
const AgentActivityMap = lazy(() => import("@/components/agents/AgentActivityMap"));

export default function AgentProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 5, reviewer_name: user?.name || "", comment: "" });
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [reviewError, setReviewError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [imgError, setImgError] = useState(false);

  const loadReviews = async () => {
    setReviewError("");
    try {
      const res = await api.get(`/agents/${id}/reviews`);
      const items = res?.data?.items || res?.data?.data || res?.data || [];
      setReviews(Array.isArray(items) ? items : []);
    } catch (e) {
      setReviews([]);
      setReviewError(formatApiError(e));
    }
  };

  useEffect(() => {
    setData(null);
    setLoadError("");
    api.get(`/agents/${id}`).then(({ data }) => setData(data)).catch((err) => setLoadError(formatApiError(err)));
    loadReviews();
  }, [id, reloadKey]);

  useEffect(() => {
    if (user) {
      setReviewForm(prev => ({ ...prev, reviewer_name: user.name || "" }));
    }
  }, [user]);

  if (loadError) return (
    <div className="min-h-[65vh] grid place-items-center px-6 text-center">
      <div><Building2 className="w-10 h-10 mx-auto text-muted-foreground" /><h1 className="font-display text-2xl font-bold mt-4">Agent profile unavailable</h1><p className="text-sm text-muted-foreground mt-2">{loadError}</p><Button type="button" className="mt-5" onClick={() => setReloadKey((key) => key + 1)}><RefreshCw className="w-4 h-4 mr-2" />Try again</Button></div>
    </div>
  );
  if (!data) return (
    <div className="min-h-[65vh] bg-background" aria-label="Loading agent profile">
      <section className="bg-secondary py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-3 gap-8">
          <Skeleton className="h-40 w-40 rounded-2xl" />
          <div className="lg:col-span-2 space-y-4"><Skeleton className="h-3 w-28" /><Skeleton className="h-12 max-w-lg" /><Skeleton className="h-4 max-w-2xl" /><Skeleton className="h-20 max-w-lg" /></div>
        </div>
      </section>
    </div>
  );
  const { agent = {}, properties = [] } = data;

  const avgRating = reviews.length > 0
    ? (reviews.reduce((acc, r) => acc + Number(r.rating || 0), 0) / reviews.length).toFixed(1)
    : null;
  const reviewerRole = String(user?.role || "").toLowerCase();
  const canReview = reviewerRole === "buyer" || reviewerRole === "owner";

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      setReviewModalOpen(false);
      navigate(`/login?redirect=${encodeURIComponent(`/agents/${id}`)}`);
      return;
    }
    setReviewSubmitting(true);
    try {
      await api.post(`/agents/${id}/reviews`, {
        rating: reviewForm.rating,
        comment: reviewForm.comment,
      });
      toast.success("Review submitted for moderation");
      setReviewModalOpen(false);
      setReviewForm({ rating: 5, reviewer_name: user?.name || "", comment: "" });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setReviewSubmitting(false);
    }
  };

  return (
    <div className="bg-background min-h-screen" data-testid="agent-profile-page">
      <section className="bg-secondary py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-3 gap-8 items-start">
          <div className="lg:col-span-1">
            <div className="w-40 h-40 rounded-2xl bg-gradient-to-br from-primary/15 to-background overflow-hidden border border-border ring-2 ring-primary/10 mb-6 grid place-items-center text-primary font-display font-black text-4xl shadow-sm">
              {agent.picture && !imgError ? (
                <img src={prefixUrl(agent.picture)} alt={agent.name} decoding="async" onError={() => setImgError(true)} className="w-full h-full object-cover"/>
              ) : (
                agent.name ? agent.name.split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase() : "SH"
              )}
            </div>
          </div>
          <div className="lg:col-span-2">
            <p className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground mb-2"><BadgeCheck className="h-4 w-4 text-emerald-600" /> Verified Agent</p>
            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-primary">{agent.name}</h1>
            {agent.bio && <p className="text-muted-foreground mt-4 max-w-2xl leading-relaxed">{agent.bio}</p>}
            <div className="mt-6 flex flex-wrap gap-4 text-sm">
              {agent.city && <span className="flex items-center gap-1.5 font-semibold text-primary"><MapPin className="w-4 h-4"/>{agent.city}</span>}
            </div>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-xl">
              <Stat n={properties.length} l="Listings"/>
              <Stat n={Number(agent.verified_transactions || 0)} l="Verified Deals"/>
              <Stat n={agent.experience_years || "—"} l="Years"/>
              <Stat n={avgRating ? `${avgRating} ★` : "New"} l={`Rating (${reviews.length})`}/>
            </div>
          </div>
        </div>
      </section>

      {/* V3 §61: property activity map with Active/Sold layers */}
      <section className="py-12 sm:py-16" data-testid="agent-map-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-6">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-muted-foreground">Property activity</p>
            <h2 className="mt-1 font-display text-3xl font-bold text-primary">{agent.name ? `${agent.name.split(" ")[0]}'s market activity` : "Market activity"}</h2>
            <p className="mt-1 text-sm text-muted-foreground">Active listings and verified sold transactions on one map.</p>
          </div>
          <Suspense fallback={<div className="grid h-[380px] place-items-center rounded-2xl border border-border bg-secondary/20"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>}>
            <AgentActivityMap agentId={agent.user_id || agent.id || id} agentName={agent.name} />
          </Suspense>
        </div>
      </section>

      <section className="py-12 sm:py-16 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <h2 className="font-display text-3xl font-bold text-primary mb-8">Listings by {agent.name.split(" ")[0]}</h2>
          {properties.length === 0 ? (
            <p className="text-muted-foreground">No active listings.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {properties.map(p => <PropertyCard key={p.property_id || p.id} property={p}/>)}
            </div>
          )}
        </div>
      </section>

      {/* Reviews & Ratings Section */}
      <section className="py-12 bg-card">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h2 className="font-display text-3xl font-bold text-foreground">Published Client Reviews</h2>
              <p className="text-xs text-muted-foreground mt-1">Moderated ratings submitted by registered buyer and owner accounts.</p>
            </div>
            <Button onClick={() => canReview ? setReviewModalOpen(true) : !user ? navigate(`/login?redirect=${encodeURIComponent(`/agents/${id}`)}`) : toast.info("Only verified buyer and owner accounts can review agents")} title="Write a verified client review" className="gap-2 font-bold text-xs" data-testid="write-review-btn">
              <MessageSquarePlus className="w-4 h-4" /> Write a Review
            </Button>
          </div>

          {reviewError ? (
            <div className="text-center py-10 border border-destructive/30 bg-destructive/5 rounded-2xl p-6" role="alert">
              <p className="font-semibold text-sm">Reviews could not be loaded.</p>
              <p className="text-xs text-muted-foreground mt-1">{reviewError}</p>
              <Button type="button" variant="outline" size="sm" className="mt-4" onClick={loadReviews}>Try again</Button>
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-12 border border-dashed border-border rounded-2xl p-6 text-muted-foreground space-y-2">
              <p className="font-semibold text-sm">No reviews submitted for this agent yet.</p>
              <p className="text-xs">Be the first to share your experience!</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {reviews.map((r) => (
                <div key={r.id} className="p-4 bg-background border border-border/80 rounded-2xl shadow-sm space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-sm text-foreground">{r.reviewer_name || "Buyer or owner"}</span>
                    <div className="flex items-center gap-1 text-amber-500 font-bold text-xs">
                      <Star className="w-3.5 h-3.5 fill-amber-500" /> {r.rating} / 5
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{r.comment}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Review Dialog Modal */}
      <Dialog open={reviewModalOpen} onOpenChange={setReviewModalOpen}>
        <DialogContent className="sm:max-w-md bg-card text-foreground rounded-2xl p-6 shadow-2xl">
          <DialogHeader>
            <DialogTitle className="font-bold text-lg">Write a Review for {agent.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleReviewSubmit} className="space-y-4 pt-2">
            <div>
              <Label className="text-xs font-bold mb-1 block">Your Name</Label>
              <Input
                value={reviewForm.reviewer_name}
                readOnly
                placeholder="Name from your verified account"
                className="text-xs"
              />
            </div>

            <div>
              <Label className="text-xs font-bold mb-1 block">Rating (1 to 5 Stars)</Label>
              <select
                value={reviewForm.rating}
                onChange={(e) => setReviewForm({ ...reviewForm, rating: Number(e.target.value) })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-xs"
              >
                <option value="5">5 Stars — Excellent</option>
                <option value="4">4 Stars — Very Good</option>
                <option value="3">3 Stars — Average</option>
                <option value="2">2 Stars — Poor</option>
                <option value="1">1 Star — Terrible</option>
              </select>
            </div>

            <div>
              <Label className="text-xs font-bold mb-1 block">Your Review & Comments</Label>
              <textarea
                value={reviewForm.comment}
                onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                required
                minLength={3}
                maxLength={2000}
                rows={3}
                placeholder="Share your experience working with this agent..."
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-xs"
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setReviewModalOpen(false)} disabled={reviewSubmitting} title="Cancel review">
                Cancel
              </Button>
              <Button type="submit" disabled={reviewSubmitting} title="Submit this review for moderation" className="font-bold text-xs">
                {reviewSubmitting ? "Submitting..." : "Submit Review"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ n, l }) {
  return (
    <div className="border border-border rounded-md p-4 bg-background hover:border-primary/30 hover:-translate-y-0.5 hover:shadow-sm transition-all duration-200">
      <p className="font-display font-black text-xl text-primary tabular-nums truncate">{n}</p>
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{l}</p>
    </div>
  );
}
